/**
 * `/sales/receipts` — receipts ledger (S17 · sales-side mirror of /payments).
 *
 * V1.0 scope: read-only table of receipts (newest first), tenant-scoped via
 * RLS. Primary entry to RECORD a receipt is from the invoice detail page;
 * this surface is for browsing the history.
 *
 * Chrome follows the prototype design system (page-head + accent-stripe KPI
 * tiles + table-card pattern) — mirrors `/procurement/payments` so the sales-side and
 * purchase-side ledgers feel like a matched pair. Avatar + sub-line matches
 * the customers + invoices lists.
 *
 * ADR-0019: rows now surface the source-invoice currency + realised
 * settlement rate so foreign receipts read at-a-glance as USD/EUR/AED with
 * the FX gain/loss column lit up.
 *
 * @see prototype/README.md — S17 / `/sales/receipts` mapping
 * @see docs/spec/brand-spec.md §2 (colors) · §6 (cards) · §11 (layout)
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  Coins,
  Download,
  Globe,
  Search,
  ShieldCheck,
  TrendingUp,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { Alert, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { formatDate } from "../../../../lib/format.ts";
import { avatarClassFor, initialsFor } from "../../../../features/master-list/index.ts";

const receiptSearchSchema = z.object({
  invoiceId: z.string().uuid().optional(),
});

export const Route = createFileRoute("/(workspace)/sales/receipts/")({
  validateSearch: (search) => {
    const parsed = receiptSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: ReceiptsPage,
});

const ALL_MODES = ["upi", "neft", "rtgs", "imps", "cheque", "cash", "card", "other"] as const;
type Mode = (typeof ALL_MODES)[number];

const RANGE_PRESETS: Record<
  string,
  { label: string; dateFrom: string | undefined; dateTo: string | undefined }
> = {
  all: { label: "All time", dateFrom: undefined, dateTo: undefined },
  this_month: { label: "This month", dateFrom: firstOfMonth(), dateTo: undefined },
  last_30: { label: "Last 30 days", dateFrom: daysAgo(30), dateTo: undefined },
  last_90: { label: "Last 90 days", dateFrom: daysAgo(90), dateTo: undefined },
};
type RangeKey = keyof typeof RANGE_PRESETS;

function ReceiptsPage() {
  const navigate = useNavigate();
  const { invoiceId } = Route.useSearch();
  const [searchInput, setSearchInput] = useState("");
  const [mode, setMode] = useState<Mode | "">("");
  const [range, setRange] = useState<RangeKey>(() => (invoiceId ? "all" : "this_month"));

  const debouncedSearch = useDebounced(searchInput, 300);

  useEffect(() => {
    if (invoiceId) setRange("all");
  }, [invoiceId]);

  // The receipts.list contract doesn't yet support server-side search /
  // mode / date-range filters (a small follow-up). For now we filter the
  // visible page client-side — the typical receipts list is < 50 rows.
  const list = trpc.receipts.list.useQuery({
    limit: 50,
    ...(invoiceId ? { invoiceId } : {}),
  });
  const allItems = list.data?.items ?? [];

  const items = useMemo(() => {
    const preset = RANGE_PRESETS[range] ?? RANGE_PRESETS.all!;
    const needle = debouncedSearch.trim().toLowerCase();
    return allItems.filter((r) => {
      if (mode && r.mode !== mode) return false;
      if (preset.dateFrom && r.receivedOn < preset.dateFrom) return false;
      if (preset.dateTo && r.receivedOn > preset.dateTo) return false;
      if (needle) {
        const hay = `${r.customerName} ${r.reference ?? ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [allItems, mode, range, debouncedSearch]);

  const hasActiveFilter =
    searchInput !== "" || mode !== "" || range !== "this_month" || invoiceId !== undefined;

  const totals = items.reduce(
    (acc, r) => ({
      applied: acc.applied + BigInt(r.appliedPaise),
      cash: acc.cash + BigInt(r.amountPaise),
      tds: acc.tds + BigInt(r.tdsPaise),
      fxGain: acc.fxGain + BigInt(r.fxGainLossPaise),
    }),
    { applied: 0n, cash: 0n, tds: 0n, fxGain: 0n },
  );
  const customerCount = new Set(items.map((r) => r.customerId)).size;
  const foreignCount = items.filter((r) => r.currencyCode !== "INR").length;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Sales" }, { label: "Receipts" }]}>
      <div className="flex flex-col gap-4">
        {/* Page head */}
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-ink">Receipts ledger</h1>
            <p className="text-sm text-muted mt-0.5">
              Customer payments allocated to invoices. Record a receipt from any invoice's detail
              page.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                // Client-side CSV export of currently-visible filtered rows
                // (AC34/AC35/AC36). Respects active search/mode/range/invoice
                // filters because `items` is the filtered view.
                if (items.length === 0) return;
                const headers = [
                  "customer",
                  "received_on",
                  "mode",
                  "reference",
                  "cash_rupees",
                  "tds_rupees",
                  "applied_rupees",
                  "fx_gain_loss_rupees",
                  "currency_code",
                ];
                const escape = (v: string): string =>
                  /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                const rupees = (paise: string): string => (Number(paise) / 100).toFixed(2);
                const rows = items.map((r) =>
                  [
                    escape(r.customerName ?? ""),
                    r.receivedOn,
                    r.mode,
                    escape(r.reference ?? ""),
                    rupees(r.amountPaise),
                    rupees(r.tdsPaise),
                    rupees(r.appliedPaise),
                    rupees(r.fxGainLossPaise),
                    r.currencyCode,
                  ].join(","),
                );
                const csv = [headers.join(","), ...rows].join("\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `receipts-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
              disabled={items.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-semibold border border-line bg-paper text-ink hover:bg-bg hover:border-line-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={13} strokeWidth={2} />
              Export
            </button>
            <Link
              to="/sales/invoices"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-semibold border border-ink bg-ink text-paper hover:bg-ink-2 transition-colors"
              title="Record a receipt from any invoice's detail page"
            >
              <ArrowRight size={13} strokeWidth={2.4} />
              Go to an invoice
            </Link>
          </div>
        </header>

        {/* KPI strip */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
          aria-label="Receipts summary"
        >
          <KpiTile
            accent="emerald"
            label={`Applied · ${(RANGE_PRESETS[range] ?? RANGE_PRESETS.all!).label.toLowerCase()}`}
            value={formatRupeesShort(totals.applied)}
            meta={`across ${customerCount} ${customerCount === 1 ? "customer" : "customers"} · ${items.length} ${items.length === 1 ? "receipt" : "receipts"}`}
            icon={<Coins size={13} strokeWidth={2} />}
          />
          <KpiTile
            accent="indigo"
            label="Cash to bank"
            value={formatRupeesShort(totals.cash)}
            meta="Actual INR landed (after TDS + bank charges)"
            icon={<TrendingUp size={13} strokeWidth={2} />}
          />
          <KpiTile
            accent="amber"
            label="TDS withheld"
            value={formatRupees(totals.tds)}
            meta={
              totals.tds > 0n
                ? "Customer-side TDS · captured at receipt time"
                : "No TDS deducted in this view"
            }
            icon={<ShieldCheck size={13} strokeWidth={2} />}
          />
          <KpiTile
            accent={foreignCount > 0 ? (totals.fxGain >= 0n ? "emerald" : "rose") : "muted"}
            label="FX gain / loss"
            value={foreignCount > 0 ? formatSignedRupees(totals.fxGain) : "—"}
            meta={
              foreignCount > 0
                ? `${foreignCount} foreign ${foreignCount === 1 ? "receipt" : "receipts"} · booked vs realised`
                : "No foreign-currency receipts in this view"
            }
            icon={<Globe size={13} strokeWidth={2} />}
          />
        </section>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 h-9 bg-paper border border-line rounded-sm px-3 flex-1 min-w-[280px] max-w-[420px] focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100">
            <Search size={14} className="text-muted" strokeWidth={2} />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search customer or reference…"
              aria-label="Search receipts"
              className="flex-1 bg-transparent border-0 outline-none text-sm text-ink placeholder:text-muted"
            />
            {searchInput.length > 0 ? (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                aria-label="Clear search"
                className="text-muted hover:text-ink"
              >
                <X size={13} strokeWidth={2.4} />
              </button>
            ) : null}
          </label>
          <FilterSelect
            label="Mode"
            value={mode}
            onChange={(v) => setMode(v as Mode | "")}
            options={[
              { value: "", label: "All modes" },
              ...ALL_MODES.map((m) => ({ value: m, label: m.toUpperCase() })),
            ]}
          />
          <FilterSelect
            label="Range"
            value={range}
            onChange={(v) => setRange(v as RangeKey)}
            options={Object.entries(RANGE_PRESETS).map(([k, v]) => ({ value: k, label: v.label }))}
          />
          {hasActiveFilter ? (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setMode("");
                setRange("this_month");
                void navigate({ to: "/sales/receipts", search: {} });
              }}
              className="inline-flex items-center gap-1 text-xs text-sky-700 font-semibold hover:underline px-1"
            >
              <X size={11} strokeWidth={2.4} />
              Clear filters
            </button>
          ) : null}
          {invoiceId ? (
            <span className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-sky-200 bg-sky-50 px-3 text-xs font-semibold text-sky-700">
              Invoice receipts
            </span>
          ) : null}
        </div>

        {/* Table card */}
        {list.isLoading ? (
          <p className="text-sm text-muted">Loading receipts…</p>
        ) : list.error ? (
          <Alert intent="bad" title="Couldn't load receipts" description={list.error.message} />
        ) : items.length === 0 ? (
          <EmptyState hasFilter={hasActiveFilter} />
        ) : (
          <section
            className="overflow-hidden rounded-md border border-line bg-paper shadow-sm"
            aria-label="Receipts list"
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] table-fixed text-sm">
                <colgroup>
                  <col />
                  <col className="w-32" />
                  <col className="w-24" />
                  <col className="w-44" />
                  <col className="w-32" />
                  <col className="w-28" />
                  <col className="w-32" />
                  <col className="w-28" />
                </colgroup>
                <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-4 text-left font-medium">Customer</th>
                    <th className="px-3 py-4 text-left font-medium">Received</th>
                    <th className="px-3 py-4 text-left font-medium">Mode</th>
                    <th className="px-3 py-4 text-left font-medium">Reference</th>
                    <th className="px-3 py-4 text-right font-medium">Cash</th>
                    <th className="px-3 py-4 text-right font-medium">TDS</th>
                    <th className="px-3 py-4 text-right font-medium">Applied</th>
                    <th className="px-3 py-4 text-right font-medium">FX g/l</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-t border-line hover:bg-raised">
                      {/* Customer cell — avatar + sub-line + drill-through link */}
                      <td className="px-4 py-3 align-middle">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span
                            aria-hidden
                            className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClassFor(r.customerName)}`}
                          >
                            {initialsFor(r.customerName)}
                          </span>
                          <div className="min-w-0">
                            <Link
                              to="/sales/receipts/$receiptId"
                              params={{ receiptId: r.id }}
                              className="block truncate font-semibold text-ink hover:underline"
                            >
                              {r.customerName}
                            </Link>
                            <p className="truncate text-xs text-muted">{customerSubLine(r)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle font-mono tabular-nums text-muted">
                        {formatDate(r.receivedOn)}
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <span className="inline-flex items-center rounded-sm border border-line bg-raised px-2 py-0.5 font-mono text-xs font-semibold uppercase tracking-wider text-ink-2">
                          {r.mode}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <span className="block max-w-[11rem] truncate font-mono text-xs tabular-nums text-muted">
                          {r.reference || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-middle text-right font-mono font-semibold tabular-nums text-ink">
                        {formatRupees(BigInt(r.amountPaise))}
                      </td>
                      <td className="px-3 py-3 align-middle text-right font-mono tabular-nums">
                        {r.tdsPaise === "0" ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <span className="text-amber-700">
                            {formatRupees(BigInt(r.tdsPaise))}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 align-middle text-right font-mono font-semibold tabular-nums text-ink">
                        {formatRupees(BigInt(r.appliedPaise))}
                      </td>
                      <td className="px-3 py-3 align-middle text-right font-mono tabular-nums">
                        {r.currencyCode === "INR" ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <FxCell paise={BigInt(r.fxGainLossPaise)} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer 1 — visible-set stats, aligned with the invoices table pattern. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
              <span>
                Visible{" "}
                <strong className="font-mono font-bold tabular-nums text-ink">{items.length}</strong>
              </span>
              <span aria-hidden className="h-3 w-px bg-line-2" />
              <span>
                Cash{" "}
                <strong className="font-mono font-bold tabular-nums text-ink">
                  {formatRupees(totals.cash)}
                </strong>
              </span>
              <span aria-hidden className="h-3 w-px bg-line-2" />
              <span>
                Applied{" "}
                <strong className="font-mono font-bold tabular-nums text-ink">
                  {formatRupees(totals.applied)}
                </strong>
              </span>
              <span aria-hidden className="h-3 w-px bg-line-2" />
              <span>
                TDS{" "}
                <strong className="font-mono font-bold tabular-nums text-ink">
                  {formatRupees(totals.tds)}
                </strong>
              </span>
            </div>

            {/* Footer 2 — pagination + sort caption, matching invoices/customers chrome. */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
              <span>
                Showing{" "}
                <strong className="font-mono font-bold tabular-nums text-ink">
                  {items.length === 0 ? "0" : `1–${items.length}`}
                </strong>{" "}
                of{" "}
                <strong className="font-mono font-bold tabular-nums text-ink">
                  {allItems.length}
                </strong>{" "}
                · sorted by creation order (newest first)
                {hasActiveFilter ? " · filtered" : ""}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled
                  aria-label="Previous page"
                  className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft size={12} strokeWidth={2.4} />
                </button>
                <button
                  type="button"
                  aria-current="page"
                  className="grid h-7 min-w-[28px] place-items-center rounded-sm border border-sky-600 bg-sky-600 px-2 text-xs font-semibold text-white"
                >
                  1
                </button>
                <button
                  type="button"
                  disabled={!list.data?.nextCursor}
                  aria-label="Next page"
                  className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronRight size={12} strokeWidth={2.4} />
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ── Customer sub-line ─────────────────────────────────────────────

function customerSubLine(r: { mode: string; currencyCode: string }): string {
  const parts: string[] = [];
  if (r.currencyCode !== "INR") {
    parts.push(`${r.currencyCode} receipt`);
  }
  parts.push(r.mode === "cash" ? "Cash" : r.mode.toUpperCase());
  return parts.join(" · ");
}

// ── Filter select ─────────────────────────────────────────────────

const EMPTY_FILTER_VALUE = "__empty__";

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  const isActive = value !== "" && value !== "this_month";
  const selectValue = value === "" ? EMPTY_FILTER_VALUE : value;
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-xs uppercase tracking-wider font-semibold text-muted">{label}</span>
      <Select
        value={selectValue}
        onValueChange={(next) => onChange(next === EMPTY_FILTER_VALUE ? "" : next)}
      >
        {/* Filter trigger matches the invoices/list filter-pill chrome:
         *  36 px tall (`h-9`), rounded-sm, text-xs. Distinct from the
         *  /payments compact (h-8) variant which was originally copied
         *  here by mistake. */}
        <SelectTrigger
          aria-label={label}
          className={`!h-9 !text-xs !px-3 !rounded-sm !w-auto !min-w-[7rem] cursor-pointer ${
            isActive ? "!border-sky-600 !text-sky-700 !font-semibold" : ""
          }`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem
              key={o.value === "" ? EMPTY_FILTER_VALUE : o.value}
              value={o.value === "" ? EMPTY_FILTER_VALUE : o.value}
            >
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

// ── KPI tile ──────────────────────────────────────────────────────

type Accent = "indigo" | "emerald" | "amber" | "rose" | "muted";

const ACCENT: Record<Accent, { stripe: string; iconBg: string; iconText: string }> = {
  indigo: { stripe: "bg-sky-600", iconBg: "bg-sky-50", iconText: "text-sky-600" },
  emerald: { stripe: "bg-emerald-600", iconBg: "bg-emerald-50", iconText: "text-emerald-600" },
  amber: { stripe: "bg-amber-600", iconBg: "bg-amber-50", iconText: "text-amber-700" },
  rose: { stripe: "bg-rose-600", iconBg: "bg-rose-50", iconText: "text-rose-600" },
  muted: { stripe: "bg-line-2", iconBg: "bg-bg", iconText: "text-muted" },
};

interface KpiTileProps {
  accent: Accent;
  label: string;
  value: string;
  valueUnit?: string;
  meta?: React.ReactNode;
  icon: React.ReactNode;
}

function KpiTile({ accent, label, value, valueUnit, meta, icon }: KpiTileProps) {
  const a = ACCENT[accent];
  return (
    <div className="relative bg-paper border border-line rounded-md shadow-sm px-4 pt-3.5 pb-3 overflow-hidden transition-all hover:-translate-y-px hover:shadow-md hover:border-line-2">
      <span className={`absolute top-0 left-0 bottom-0 w-[3px] ${a.stripe}`} aria-hidden />
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted truncate">
          {label}
        </span>
        <span
          className={`inline-grid place-items-center w-[22px] h-[22px] rounded-sm ${a.iconBg} ${a.iconText}`}
        >
          {icon}
        </span>
      </div>
      <div className="text-2xl font-bold tracking-tight leading-none">
        <span className="num">{value}</span>
        {valueUnit ? (
          <span className="text-xs font-semibold text-muted ml-1">{valueUnit}</span>
        ) : null}
      </div>
      {meta ? (
        <div className="mt-1 text-xs text-muted flex items-center gap-1.5 flex-wrap">
          {meta}
        </div>
      ) : null}
    </div>
  );
}

function FxCell({ paise }: { paise: bigint }) {
  if (paise === 0n) return <span className="text-muted">—</span>;
  const positive = paise > 0n;
  return (
    <span className={positive ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>
      {positive ? "+" : ""}
      {formatSignedRupees(paise)}
    </span>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  if (hasFilter) {
    return (
      <div className="bg-paper border border-line rounded-md shadow-sm py-10 text-center">
        <p className="text-base font-medium text-ink">No receipts match these filters.</p>
        <p className="text-sm text-muted mt-1 max-w-md mx-auto">
          Widen the date range or clear the mode filter to see more.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-paper border border-line rounded-md shadow-sm py-10 text-center">
      <p className="text-base font-medium text-ink">No receipts yet.</p>
      <p className="text-sm text-muted mt-1 max-w-md mx-auto">
        Open any issued invoice and click "Record receipt" to log a customer payment. The receipt
        will appear here, and the invoice's status will flip to partially-paid or paid.
      </p>
      <Link
        to="/sales/invoices"
        className="inline-flex items-center mt-4 text-sm text-sky-700 hover:underline font-semibold"
      >
        Open invoices →
      </Link>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function firstOfMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function formatRupees(paise: bigint): string {
  const rupees = paise / 100n;
  return `₹${rupees.toLocaleString("en-IN")}`;
}

function formatSignedRupees(paise: bigint): string {
  const sign = paise < 0n ? "-" : "";
  const abs = paise < 0n ? -paise : paise;
  return `${sign}₹${(abs / 100n).toLocaleString("en-IN")}`;
}

function formatRupeesShort(paise: bigint): string {
  const rupees = paise / 100n;
  if (rupees >= 10_000_000n) return `₹${(Number(rupees) / 10_000_000).toFixed(2)}Cr`;
  if (rupees >= 100_000n) return `₹${(Number(rupees) / 100_000).toFixed(2)}L`;
  return `₹${rupees.toLocaleString("en-IN")}`;
}
